import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import Checkbox from '@sharedCR/common/Checkbox';
import FormGenerator from '@sharedCR/common/formGenerator';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

import { BaseStatus } from 'constants/loanApplication';
import { getConditionalArray } from 'utils/data';

import { selectBorrowerFieldsVisibilityBySideData } from 'store/loanApplications/selectors';

import { BorrowersTestIds } from '../../../../../Borrowers.testIds';
import { pagesPropTypes } from '../../MainContent.constants';

import { getCreditReportingConfig, getCreditReportingTableConfig } from './CreditReporting.config';
import { keyFactorsItemStyles, wrapperStyles } from './CreditReporting.styles';
import DerogatoryEventsTable from './DerogatoryEventsTable';

const isChecked = (middleFico, currentFico) =>
  !_.isNil(middleFico) && !_.isNil(currentFico) && middleFico === currentFico;

const CreditReporting = ({
  changedFields,
  isLoading,
  isSaving,
  form,
  validationErrors,
  validateField,
  handleChange,
  entityId,
  section,
  setEntityConfig,
  setInForm,
  setInitialFormValues,
  setForm,
  className,
  entityData: applicationData,
  disabledPendingChangesHighlight,
  pendingChanges,
}) => {
  const {
    positiveRentalHistory: isPositiveRentalHistoryVisible,
    caivrsNumber: isCaivrsNumberVisible,
    caivrsAuthorizationCode: isCaivrsAuthorizationCodeVisible,
  } = useSelector(selectBorrowerFieldsVisibilityBySideData);

  const { creditRating } = applicationData ?? {};
  const { equifaxFico, experianFico, transUnionFico, middleFico } = creditRating ?? {};

  const config = useMemo(() => {
    return getCreditReportingConfig({
      entityId,
      section,
      isPositiveRentalHistoryVisible,
      isCaivrsNumberVisible,
      isCaivrsAuthorizationCodeVisible,
    });
  }, [
    entityId,
    section,
    isPositiveRentalHistoryVisible,
    isCaivrsNumberVisible,
    isCaivrsAuthorizationCodeVisible,
  ]);

  const updateStateIfEmpty = useCallback(
    prev => {
      const isPositiveRentalHistoryNull =
        prev[entityId]?.creditReporting?.positiveRentalHistory === '';

      return isPositiveRentalHistoryNull
        ? {
            ...prev,
            [entityId]: {
              ...prev[entityId],
              creditReporting: {
                ...prev[entityId]?.creditReporting,
                positiveRentalHistory: BaseStatus.YES,
              },
            },
          }
        : prev;
    },
    [entityId],
  );

  useEffect(() => {
    if (!config) {
      return;
    }

    setEntityConfig(config);
  }, [config, setEntityConfig, isPositiveRentalHistoryVisible]);

  useEffect(() => {
    if (!isPositiveRentalHistoryVisible) {
      return;
    }

    setInitialFormValues(updateStateIfEmpty);
    setForm(updateStateIfEmpty);
  }, [isPositiveRentalHistoryVisible, form, setForm, setInitialFormValues, updateStateIfEmpty]);

  const formattedKeyFactors = useMemo(() => {
    if (!creditRating) {
      return [];
    }

    const { creditRatingBureaus } = creditRating;

    const biggestKeyFactorsLength = _.max(
      creditRatingBureaus?.map(bureaus => bureaus.keyFactors?.length),
    );

    if (!biggestKeyFactorsLength) {
      return [
        {
          equifax: '-',
          experian: '-',
          transUnion: '-',
        },
      ];
    }

    return Array.from({ length: biggestKeyFactorsLength }, (__, i) =>
      creditRatingBureaus.reduce((acc, cur) => {
        const key = _.lowerFirst(cur.type);

        const curKeyFactor = cur.keyFactors[i];
        const { code, description } = curKeyFactor ?? {};

        acc[key] = curKeyFactor ? (
          <Typography key={`${code}-${description}`} sx={keyFactorsItemStyles} variant="regular14">
            <span>{code}</span> - {_.capitalize(description)}
          </Typography>
        ) : (
          '-'
        );

        return acc;
      }, {}),
    );
  }, [creditRating]);

  const formattedData = useMemo(
    () => [
      ...getConditionalArray(!!creditRating, () => [
        {
          bureaus: {
            value: 'Scores',
          },
          equifax: equifaxFico ?? '-',
          experian: experianFico ?? '-',
          transUnion: transUnionFico ?? '-',
        },
        {
          bureaus: {
            value: 'UD',
          },
          equifax: (
            <Checkbox
              disabled
              checked={isChecked(middleFico, equifaxFico)}
              data-testid={BorrowersTestIds.CREDIT_REPORTING_EQUIFAX_UD_CHECKBOX}
            />
          ),
          experian: (
            <Checkbox
              disabled
              checked={isChecked(middleFico, experianFico)}
              data-testid={BorrowersTestIds.CREDIT_REPORTING_EXPERIAN_UD_CHECKBOX}
            />
          ),
          transUnion: (
            <Checkbox
              disabled
              checked={isChecked(middleFico, transUnionFico)}
              data-testid={BorrowersTestIds.CREDIT_REPORTING_TRANSUNION_UD_CHECKBOX}
            />
          ),
        },
        {
          bureaus: {
            value: 'Key Factors',
            rowSpan: formattedKeyFactors.length,
            position: 1,
          },
          ...formattedKeyFactors[0],
        },
        ...formattedKeyFactors.slice(1),
      ]),
      ...getConditionalArray(!creditRating, [
        {
          bureaus: {
            position: 1,
            value: 'Credit has not been pulled for this borrower',
            isDescription: true,
            colSpan: 4,
          },
        },
      ]),
    ],
    [equifaxFico, experianFico, transUnionFico, middleFico, formattedKeyFactors, creditRating],
  );

  if (!config) {
    return null;
  }

  return (
    <Box className={className} sx={wrapperStyles}>
      <TableCDS
        isCustomDataDefinition
        withInternalBorders
        config={getCreditReportingTableConfig()}
        data={formattedData}
        isLoading={isLoading && !isSaving}
        withDivider={false}
      />

      <DerogatoryEventsTable
        derogatoryEvents={applicationData?.declarations?.finances?.derogatoryEvents ?? null}
        isLoading={isLoading && !isSaving}
      />

      <FormGenerator
        isNestedForm
        changedFields={changedFields}
        config={config}
        disabledPendingChangesHighlight={disabledPendingChangesHighlight}
        form={form}
        formName={FormNames.CREDIT_REPORTING}
        handleChange={handleChange}
        isLoading={isSaving || isLoading}
        numberOfColumns={4}
        pendingChanges={pendingChanges}
        setInForm={setInForm}
        validateField={validateField}
        validationErrors={validationErrors}
      />
    </Box>
  );
};

CreditReporting.propTypes = pagesPropTypes;

export default memo(CreditReporting);
